/**
 * MBA Lab — pure state/markdown model for the Stakeholder Interview Builder.
 * No DOM, storage, or analytics dependencies - stakeholder-interview-
 * builder.js owns all of that and consumes getDocumentModel()/buildMarkdown()
 * from here, matching charter-model.js's exact split so the live preview,
 * the .md download, and the copy button all come from one source.
 *
 * Every user-controlled string that flows through here is untrusted: names,
 * purpose, and custom questions can contain anything. This module never
 * builds HTML - the UI file renders the document model via textContent/DOM
 * construction, never innerHTML. buildMarkdown() escapes markdown-structural
 * characters so pasted/typed content can't reshape the document.
 */

import { GOALS, QUESTIONS } from './interview-questions.js';

export const SCHEMA_NAME = 'mbalab.stakeholder-interview-builder';
export const SCHEMA_VERSION = 1;

export const LIMITS = Object.freeze({
  shortField: 120,
  mediumField: 300,
  customQuestions: 1000,
  maxSelectedQuestions: 15,
  maxSerializedBytes: 20 * 1024,
});

const QUESTIONS_BY_ID = new Map(QUESTIONS.map((q) => [q.id, q]));

function clampText(value, maxLen) {
  return typeof value === 'string' ? value.slice(0, maxLen) : '';
}

function clampSingleLineText(value, maxLen) {
  if (typeof value !== 'string') return '';
  return value.replace(/\s*[\r\n]+\s*/g, ' ').trim().slice(0, maxLen);
}

export function createInitialState() {
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: null,
    stakeholderName: '',
    stakeholderRole: '',
    purpose: '',
    logistics: '',
    selectedQuestionIds: [],
    customQuestions: '',
  };
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const rawIds = Array.isArray(r.selectedQuestionIds) ? r.selectedQuestionIds : [];
  const seen = new Set();
  const selectedQuestionIds = [];
  for (const id of rawIds) {
    if (selectedQuestionIds.length >= LIMITS.maxSelectedQuestions) break;
    if (typeof id !== 'string' || !QUESTIONS_BY_ID.has(id) || seen.has(id)) continue;
    seen.add(id);
    selectedQuestionIds.push(id);
  }

  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    stakeholderName: clampSingleLineText(r.stakeholderName, LIMITS.shortField),
    stakeholderRole: clampSingleLineText(r.stakeholderRole, LIMITS.shortField),
    purpose: clampText(r.purpose, LIMITS.mediumField),
    logistics: clampSingleLineText(r.logistics, LIMITS.mediumField),
    selectedQuestionIds,
    customQuestions: clampText(r.customQuestions, LIMITS.customQuestions),
  };
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - see charter-model.js's
  // identical note. Proves the mechanism works before it's ever needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function setStakeholderName(state, value) {
  return { ...state, stakeholderName: clampSingleLineText(value, LIMITS.shortField) };
}
export function setStakeholderRole(state, value) {
  return { ...state, stakeholderRole: clampSingleLineText(value, LIMITS.shortField) };
}
export function setPurpose(state, value) {
  return { ...state, purpose: clampText(value, LIMITS.mediumField) };
}
export function setLogistics(state, value) {
  return { ...state, logistics: clampSingleLineText(value, LIMITS.mediumField) };
}
export function setCustomQuestions(state, value) {
  return { ...state, customQuestions: clampText(value, LIMITS.customQuestions) };
}

export function isQuestionSelected(state, id) {
  return state.selectedQuestionIds.includes(id);
}

export function selectedQuestionCount(state) {
  return state.selectedQuestionIds.length;
}

export function isSelectionFull(state) {
  return state.selectedQuestionIds.length >= LIMITS.maxSelectedQuestions;
}

/** No-op (not an error) once the cap is reached - the UI disables unchecked
 * boxes at that point, so this only guards a direct/racing call. */
export function toggleQuestion(state, id) {
  if (!QUESTIONS_BY_ID.has(id)) return state;
  if (state.selectedQuestionIds.includes(id)) {
    return { ...state, selectedQuestionIds: state.selectedQuestionIds.filter((x) => x !== id) };
  }
  if (isSelectionFull(state)) return state;
  return { ...state, selectedQuestionIds: [...state.selectedQuestionIds, id] };
}

function p(text) {
  return { type: 'p', text };
}

function list(items) {
  return { type: 'list', items };
}

/**
 * The single structured representation of the interview guide that both the
 * live DOM preview and buildMarkdown() render from - text only (no HTML), so
 * the DOM-side renderer can safely use textContent for every field here.
 * Selected questions are grouped by GOALS order (not selection order), and
 * custom questions are split into individual lines, trimmed, empty ones
 * dropped - matching how every other multi-line free-text field on this site
 * treats one-item-per-line input.
 */
export function getDocumentModel(state) {
  const sections = [];

  for (const goal of GOALS) {
    const goalQuestions = QUESTIONS.filter((q) => q.goal === goal.key && state.selectedQuestionIds.includes(q.id));
    if (!goalQuestions.length) continue;
    sections.push({ key: goal.key, title: goal.label, blocks: [list(goalQuestions.map((q) => q.text))] });
  }

  const customLines = state.customQuestions
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (customLines.length) {
    sections.push({ key: 'custom', title: 'Your own questions', blocks: [list(customLines)] });
  }

  return {
    stakeholderName: state.stakeholderName,
    stakeholderRole: state.stakeholderRole,
    purpose: state.purpose,
    logistics: state.logistics,
    sections,
  };
}

/** Escapes markdown-structural characters so untrusted text can never reshape
 * the document or smuggle raw HTML through a markdown-to-HTML render. Same
 * implementation as charter-model.js/planner-model.js/journal-model.js - each
 * *-model.js file is deliberately self-contained with no cross-file
 * dependencies, so this is a 4th independent copy, not an oversight. */
export function escapeMarkdown(text) {
  const escaped = String(text)
    .replace(/\\/g, '\\\\')
    .replace(/([`*_{}[\]()#+!|<>~&])/g, '\\$1');
  return escaped
    // Up to 3 leading spaces are allowed before a block marker in CommonMark,
    // so anchoring these at column 0 alone let an *indented* bullet, setext
    // underline, or thematic break through as live structure.
    .replace(/^( {0,3})([-=>])/gm, '$1\\$2')
    // Ordered-list markers ("1. " or "1) ") are the one line-leading
    // structural form the two rules above don't already neutralize.
    .replace(/^( {0,3})(\d{1,9})\./gm, '$1$2\\.');
}

export function buildMarkdown(state) {
  const doc = getDocumentModel(state);
  const lines = [];

  lines.push(`# Interview guide: ${escapeMarkdown(doc.stakeholderName) || '[Stakeholder name]'}`);
  const roleAndLogistics = [doc.stakeholderRole, doc.logistics].filter(Boolean).map(escapeMarkdown).join(' — ');
  if (roleAndLogistics) lines.push(`*${roleAndLogistics}*`);
  lines.push('');

  lines.push('## Purpose');
  lines.push('');
  lines.push(escapeMarkdown(doc.purpose) || '[Add the purpose of this interview]');
  lines.push('');

  if (!doc.sections.length) {
    lines.push('_No questions selected yet._');
    lines.push('');
  }

  for (const section of doc.sections) {
    lines.push(`## ${section.title}`);
    lines.push('');
    for (const block of section.blocks) {
      if (block.type === 'list') {
        for (const item of block.items) lines.push(`- ${escapeMarkdown(item)}`);
        lines.push('');
      }
    }
  }

  lines.push('---');
  lines.push('*Built with the free MBA Lab stakeholder interview builder - mbalab.net/tools/stakeholder-interview-builder*');

  return lines.join('\n');
}
